import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Stethoscope } from "lucide-react";
import { CtaButton } from "./CtaButton";

const LINKS = [
  { label: "The Symptoms", to: "/#symptoms" },
  { label: "Insights", to: "/insights" },
  { label: "About", to: "/about" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 z-50 w-full transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#0E1526]/70 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="w-full px-6 lg:px-10 2xl:px-16 flex items-center justify-between h-[88px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
            <Stethoscope size={26} strokeWidth={1.25} className="text-[#5CCBF5]" />
            <span className="leading-[0.95]">
              <span className="block font-mono text-[15px] sm:text-[16px] tracking-[0.2em] text-[#f5f3ee] whitespace-nowrap">THE AI DOCTOR</span>
              <span className="block font-mono text-[10px] tracking-[0.24em] text-[#5CCBF5] mt-0.5">DIAGNOSIS FIRST.</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-underline font-mono text-[12px] tracking-[0.1em] uppercase text-[#a7a7a2] hover:text-[#f5f3ee] transition-colors duration-300 whitespace-nowrap"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden xl:block">
            {location.pathname !== "/apply" && (
              <CtaButton to="/apply" testid="nav-cta" className="!px-6 !py-3">
                Get Your Free AI Check-Up
              </CtaButton>
            )}
          </div>

          <button
            className="xl:hidden text-[#f5f3ee] p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#0E1526] xl:hidden pt-[88px]"
            data-testid="mobile-menu"
          >
            <div className="shell flex flex-col gap-2 py-10">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    className="block py-4 border-b border-white/10 text-2xl display text-[#f5f3ee]"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              {location.pathname !== "/apply" && (
                <div className="pt-8">
                  <CtaButton to="/apply" testid="mobile-nav-cta">
                    Get Your Free AI Check-Up
                  </CtaButton>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
